<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useDatatablesStore } from '../store/index.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const store = useDatatablesStore();

const FIELD_TYPES = computed(() => [
  { value: 'text', label: t('DATATABLES.FIELD_TYPES.TEXT') },
  { value: 'number', label: t('DATATABLES.FIELD_TYPES.NUMBER') },
  { value: 'boolean', label: t('DATATABLES.FIELD_TYPES.BOOLEAN') },
  { value: 'date', label: t('DATATABLES.FIELD_TYPES.DATE') },
  { value: 'datetime', label: t('DATATABLES.FIELD_TYPES.DATETIME') },
  { value: 'select', label: t('DATATABLES.FIELD_TYPES.SELECT') },
  { value: 'email', label: t('DATATABLES.FIELD_TYPES.EMAIL') },
  { value: 'url', label: t('DATATABLES.FIELD_TYPES.URL') },
]);

const name = ref('');
const description = ref('');
const fields = ref([]);
const isSaving = ref(false);

function addField() {
  fields.value.push({ name: '', type: 'text', required: false });
}

function removeField(idx) {
  fields.value.splice(idx, 1);
}

async function onSave() {
  isSaving.value = true;
  try {
    const table = await store.createTable({
      name: name.value,
      description: description.value,
      schema: fields.value,
    });
    router.push(accountScopedRoute('datatables_show', { slug: table.slug }));
  } catch (e) {
    const msg =
      e?.response?.data?.errors?.join(', ') || t('DATATABLES.NEW.ERROR');
    useAlert(msg);
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <div class="flex w-full h-full flex-col overflow-hidden bg-n-surface-1">

    <header class="sticky top-0 z-10 px-6">
      <div class="flex items-center justify-between w-full py-6 gap-2 mx-auto max-w-5xl">
        <div class="flex items-center gap-3 min-w-0">
          <button
            class="flex items-center justify-center size-8 shrink-0 rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
            @click="router.push(accountScopedRoute('datatables_index'))"
          >
            <span class="i-lucide-arrow-left size-4" />
          </button>
          <span class="text-xl font-medium truncate text-n-slate-12">{{
            t('DATATABLES.NEW.TITLE')
          }}</span>
        </div>
        <Button
          :label="t('DATATABLES.NEW.CREATE_TABLE')"
          size="sm"
          class="flex-shrink-0"
          :disabled="!name.trim() || isSaving"
          :is-loading="isSaving"
          @click="onSave"
        />
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6 pb-10">
      <div class="w-full mx-auto max-w-5xl flex flex-col gap-6">

        <section class="flex flex-col gap-4 p-6 rounded-xl outline outline-1 outline-n-weak -outline-offset-1">
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('DATATABLES.NEW.BASIC_INFO') }}
          </h2>
          <div class="flex flex-col gap-4 max-w-lg">
            <Input
              v-model="name"
              :label="t('DATATABLES.NEW.NAME_LABEL')"
              :placeholder="t('DATATABLES.NEW.NAME_PLACEHOLDER')"
            />
            <Input
              v-model="description"
              :label="t('DATATABLES.NEW.DESCRIPTION_LABEL')"
              :placeholder="t('DATATABLES.NEW.DESCRIPTION_PLACEHOLDER')"
            />
          </div>
        </section>

        <section class="flex flex-col gap-4 p-6 rounded-xl outline outline-1 outline-n-weak -outline-offset-1">
          <div class="flex items-center justify-between">
            <div class="flex flex-col gap-0.5">
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ t('DATATABLES.NEW.COLUMNS') }}
              </h2>
              <p class="text-xs text-n-slate-10">
                {{ t('DATATABLES.NEW.COLUMNS_DESCRIPTION') }}
              </p>
            </div>
            <Button
              color="slate"
              size="sm"
              icon="i-lucide-plus"
              :label="t('DATATABLES.NEW.ADD_COLUMN')"
              @click="addField"
            />
          </div>

          <div v-if="fields.length === 0" class="flex flex-col items-center gap-2 py-8 text-center">
            <span class="i-lucide-columns size-8 text-n-slate-7" />
            <p class="text-sm text-n-slate-10">
              {{ t('DATATABLES.NEW.EMPTY_TITLE') }}
            </p>
            <p class="text-xs text-n-slate-9">
              {{ t('DATATABLES.NEW.EMPTY_HINT') }}
            </p>
          </div>

          <div v-else class="flex flex-col gap-2">
            <div class="grid grid-cols-[1fr_180px_auto_32px] gap-3 px-3 pb-1">
              <span class="text-xs font-medium text-n-slate-10">{{ t('DATATABLES.NEW.FIELD_NAME') }}</span>
              <span class="text-xs font-medium text-n-slate-10">{{ t('DATATABLES.NEW.TYPE') }}</span>
              <span class="text-xs font-medium text-n-slate-10">{{ t('DATATABLES.NEW.REQUIRED') }}</span>
              <span />
            </div>
            <div
              v-for="(field, idx) in fields"
              :key="idx"
              class="grid grid-cols-[1fr_180px_auto_32px] gap-3 items-center px-3 py-2 rounded-lg bg-n-alpha-black2"
            >
              <Input v-model="field.name" size="sm" placeholder="nome_do_campo" />
              <Select v-model="field.type" :options="FIELD_TYPES" />
              <label class="flex items-center justify-center cursor-pointer">
                <input v-model="field.required" type="checkbox" class="rounded" />
              </label>
              <button
                class="flex items-center justify-center size-8 rounded-lg text-n-slate-9 hover:bg-n-ruby-3 hover:text-n-ruby-11 transition-colors"
                @click="removeField(idx)"
              >
                <span class="i-lucide-trash-2 size-4" />
              </button>
            </div>
          </div>
        </section>

      </div>
    </main>

  </div>
</template>
